import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { ApiError, api } from '@/lib/api';
import { useUrlState } from '@/lib/useUrlState';
import ParentEditModal from './ParentEditModal';

/**
 * Реестр user-родителей (users.role='parent').
 *
 * В отличие от карточки контакта ребёнка (она в `contacts`) — здесь видим
 * глобальный объект «родитель», к которому может быть привязано сразу
 * несколько детей и один «человек в СКУД». Удобно для отдела безопасности:
 * выдать пропуск родителю можно один раз — на всех его детей.
 *
 * Фильтры синхронизируются с URL (см. URL_SCHEMA). По умолчанию показываем
 * только активных.
 */
const URL_SCHEMA = { q: '', active: '1', skud: '', edit: '' };

export default function ParentsPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canRead  = can('staff.parents', 'read');
  const canWrite = can('staff.parents', 'write');

  const [params, setParams] = useUrlState(URL_SCHEMA);
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const reload = () => {
    setLoading(true); setError(null);
    const qs = new URLSearchParams();
    if (params.q)      qs.set('q', params.q);
    if (params.active) qs.set('active', params.active);
    if (params.skud)   qs.set('hasSkud', params.skud);
    qs.set('limit', '500');
    api.get('/api/parents?' + qs.toString())
      .then((res) => setRows(res.rows || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [params.q, params.active, params.skud]);

  const editing = useMemo(() => {
    if (!params.edit) return null;
    const id = parseInt(params.edit, 10);
    return rows.find((r) => r.id === id) || { id, _stub: true };
  }, [params.edit, rows]);

  if (!canRead) {
    return (
      <>
        <Topbar
          breadcrumb={['Администрирование', 'Родители']}
          density={tweaks.density}
          onDensityChange={(d) => updateTweak('density', d)}
        />
        <div className="ref-empty" style={{ padding: 32 }}>Нет доступа к реестру родителей.</div>
      </>
    );
  }

  return (
    <>
      <Topbar
        breadcrumb={['Администрирование', 'Родители']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="ref-page">
        <div className="ref-toolbar">
          <span className="ref-toolbar__title">
            Родители {!loading && <span className="muted">· {rows.length}</span>}
          </span>
          <input
            className="ref-toolbar__search"
            placeholder="Поиск: ФИО, email, telegram, ФИО ребёнка"
            value={params.q || ''}
            onChange={(e) => setParams({ q: e.target.value })}
            style={{
              marginLeft: 16, height: 30, padding: '0 10px',
              border: '1px solid var(--border-strong)', borderRadius: 6,
              fontSize: 13, background: 'var(--panel)', color: 'var(--fg)',
              minWidth: 260,
            }}
          />
          <select
            value={params.skud || ''}
            onChange={(e) => setParams({ skud: e.target.value })}
            title="Фильтр по привязке к человеку в СКУД"
            style={{
              marginLeft: 8, height: 30, padding: '0 8px',
              border: '1px solid var(--border-strong)', borderRadius: 6,
              fontSize: 13, background: 'var(--panel)', color: 'var(--fg)',
            }}
          >
            <option value="">СКУД: все</option>
            <option value="1">С привязкой к СКУД</option>
            <option value="0">Без привязки</option>
          </select>
          <select
            value={params.active || '1'}
            onChange={(e) => setParams({ active: e.target.value })}
            style={{
              marginLeft: 8, height: 30, padding: '0 8px',
              border: '1px solid var(--border-strong)', borderRadius: 6,
              fontSize: 13, background: 'var(--panel)', color: 'var(--fg)',
            }}
          >
            <option value="1">Активные</option>
            <option value="0">В архиве</option>
            <option value="">Все</option>
          </select>
          <div className="ref-toolbar__spacer" />
          <span className="muted" style={{ fontSize: 12 }}>
            Создание родителя — через карточку контакта ученика.
          </span>
        </div>

        <div className="ref-content">
          {error && (
            <div style={{ color: 'var(--red-fg)', padding: 16 }}>Ошибка: {error}</div>
          )}
          {loading ? (
            <div className="ref-empty">Загрузка…</div>
          ) : rows.length === 0 ? (
            <div className="ref-empty">Никого не найдено. Поправь фильтры.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}></th>
                    <th>ФИО</th>
                    <th style={{ width: 240 }}>Email</th>
                    <th>Дети</th>
                    <th style={{ width: 220 }}>СКУД</th>
                    <th style={{ width: 90 }}>Статус</th>
                    <th style={{ width: 60 }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => (
                    <tr
                      key={u.id}
                      onClick={() => setParams({ edit: String(u.id) })}
                      style={{ cursor: 'pointer' }}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        {u.skudPerson?.photoThumb ? (
                          <img
                            src={u.skudPerson.photoThumb}
                            alt=""
                            style={{
                              width: 28, height: 28, borderRadius: '50%',
                              objectFit: 'cover', display: 'block',
                            }}
                          />
                        ) : (
                          <div style={{
                            width: 28, height: 28, borderRadius: '50%',
                            background: 'var(--panel-2)',
                            display: 'grid', placeItems: 'center',
                            color: 'var(--fg-faint)', fontSize: 11, fontWeight: 600,
                          }}>
                            {initials(u.name)}
                          </div>
                        )}
                      </td>
                      <td>
                        <span style={{ fontWeight: 500 }}>{u.name}</span>
                      </td>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {u.email || <span style={{ color: 'var(--fg-faint)' }}>—</span>}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {u.students?.length ? (
                          <span style={{ fontSize: 12.5 }}>
                            {u.students.slice(0, 3).map((s, i) => (
                              <React.Fragment key={s.id}>
                                {i > 0 && <span style={{ color: 'var(--fg-faint)' }}>, </span>}
                                <Link
                                  to={`/students?student=${s.id}`}
                                  style={{
                                    color: s.status === 'archive' ? 'var(--fg-muted)' : 'var(--link, var(--fg))',
                                    textDecoration: 'none',
                                  }}
                                  title={s.status === 'archive' ? 'В архиве' : undefined}
                                >
                                  {s.name}
                                </Link>
                              </React.Fragment>
                            ))}
                            {u.studentsCount > 3 && (
                              <span style={{ color: 'var(--fg-muted)' }}>
                                {' '}и ещё {u.studentsCount - 3}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--fg-faint)' }}>—</span>
                        )}
                      </td>
                      <td>
                        {u.skudPerson ? (
                          <span
                            style={{
                              fontSize: 12.5,
                              color: u.skudPerson.isDismissed ? 'var(--fg-muted)' : 'var(--fg)',
                              textDecoration: u.skudPerson.isDismissed ? 'line-through' : 'none',
                            }}
                            title={u.skudPerson.isDismissed ? 'Уволен в СКУД' : undefined}
                          >
                            ✓ {skudFio(u.skudPerson)}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--fg-faint)' }}>не привязан</span>
                        )}
                      </td>
                      <td>
                        {u.active
                          ? <span className="sb sb--active">Активен</span>
                          : <span className="sb sb--archived">Архив</span>}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="ref-row-actions">
                          <button
                            onClick={() => setParams({ edit: String(u.id) })}
                            title={canWrite ? 'Открыть карточку' : 'Открыть карточку (просмотр)'}
                          >
                            <Icon name="edit" size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <ParentEditModal
          userId={editing.id}
          onClose={() => setParams({ edit: '' })}
          onSaved={() => reload()}
        />
      )}
    </>
  );
}

function initials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase();
}

function skudFio(sp) {
  const parts = [sp.lastName, sp.firstName, sp.middleName].filter(Boolean);
  return parts.join(' ') || `#${sp.skudId}`;
}
